import isEmpty from 'lodash/isEmpty'
import { useMemo } from 'react'

import {
  type TargetsByOwner,
  unhealthyPod
} from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { type K8sAutocompleteSearchOption } from 'components/services/create/form-controls/fingerprint/kubernetes/types'
import useAuth from 'hooks/use-auth'
import { useGetTeams } from 'hooks/use-get-teams-paged'
import { createLookupMapFromArray } from 'utils/obj'

import type { K8sObjectWithOwningTeamId } from 'types/api/kubernetes/kubernetes'

export function useGetK8sOptionsList(
  targetsByOwnerId: Record<string, TargetsByOwner<K8sObjectWithOwningTeamId>[]>
) {
  const { currentTeamId } = useAuth()
  const { data: teams } = useGetTeams()

  const teamIdMap = useMemo(
    () => createLookupMapFromArray(teams, 'identifier'),
    [teams]
  )

  return useMemo(
    () =>
      Object.entries(targetsByOwnerId).reduce<K8sAutocompleteSearchOption[]>(
        (options, [kind, targets]) => {
          const targetList = targets.map(
            ({ children, name = '', target, uid, ...targetInfo }) => {
              const unhealthyPods: string[] = []

              if (!isEmpty(children)) {
                Object.values(children)
                  ?.flatMap((child) => child)
                  .forEach((child) => {
                    // @ts-ignore TODO: Create or find this type
                    if (unhealthyPod(child) && child?.name) {
                      // @ts-ignore TODO: Create or find this type
                      unhealthyPods.push(child?.name)
                    }
                  })
              } else if (unhealthyPod(targetInfo) && name) {
                unhealthyPods.push(name)
              }

              const owningTeamId = target.owningTeamId
              const isOwner = currentTeamId === owningTeamId

              return {
                section: kind,
                label: name,
                uid,
                tags: {
                  cluster: target.clusterId,
                  namespace: target.namespace,
                  podCount: children.POD?.length,
                  replicaSetCount: children.REPLICASET?.length,
                  replicationControllerCount:
                    children.REPLICATIONCONTROLLER?.length,
                  unhealthyPods
                },
                params: {
                  kind,
                  name,
                  uid
                },
                owner: {
                  isOwner,
                  teamId: owningTeamId ?? '',
                  teamName:
                    !isOwner && owningTeamId
                      ? // if there's a problem getting the team name, just show the team id:
                        teamIdMap[owningTeamId]?.name ?? `id: ${owningTeamId}`
                      : undefined
                }
              }
            }
          )

          return [...options, ...targetList]
        },
        []
      ),
    [currentTeamId, targetsByOwnerId, teamIdMap]
  )
}
